/**
 * The desk's Activity feed, kept live (plan-frontDeskUx T7; audit P4, UX-13).
 *
 * The feed's rows come from the site event log (`GET /api/events`), which
 * since BMS-spec 0.110 holds every kind it shows — stays, housekeeping, door
 * attempts naming the lock, card changes and alarms (data-model §events
 * "Desk activity events"). The four topics below only say that something
 * happened: each message refetches the feed's queries, debounced 500 ms (at
 * most 1 s), instead of decoding a payload whose shape differs per topic. The
 * row then has one shape and needs no dedupe against the REST page.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { buildSiteRoomsTopicFilter, buildSiteTopic, createTrailingDebounce, subscribeShared, } from './useMqtt.js';
export const ACTIVITY_REFRESH_DEBOUNCE_MS = 500;
export const ACTIVITY_REFRESH_MAX_WAIT_MS = 1000;
/** The prefix every Activity query key starts with; one invalidation refreshes them all. */
export function activityQueryKey(siteCode) {
    return ['events', 'activity', siteCode];
}
/** The topics whose messages mean "the feed has a new row" (GRMS §12.1, topic-structure UI table). */
export function activityTopics(siteCode) {
    return [
        buildSiteTopic(siteCode, 'access/lock-events'),
        buildSiteTopic(siteCode, 'access/cards/events'),
        buildSiteRoomsTopicFilter(siteCode, 'alarm'),
        buildSiteRoomsTopicFilter(siteCode, 'events'),
    ];
}
// One subscription per (query client, site), however many components ask:
// the page and the sidebar's unread count both mount the hook.
const activityLive = new WeakMap();
function acquireActivityLive(queryClient, siteCode) {
    let bySite = activityLive.get(queryClient);
    if (!bySite) {
        bySite = new Map();
        activityLive.set(queryClient, bySite);
    }
    let entry = bySite.get(siteCode);
    if (!entry) {
        const refresh = createTrailingDebounce(() => {
            void queryClient.invalidateQueries({ queryKey: activityQueryKey(siteCode) });
        }, ACTIVITY_REFRESH_DEBOUNCE_MS, ACTIVITY_REFRESH_MAX_WAIT_MS);
        const unsubscribe = subscribeShared(activityTopics(siteCode), () => refresh.schedule());
        entry = { refs: 0, release: () => { refresh.cancel(); unsubscribe(); } };
        bySite.set(siteCode, entry);
    }
    const held = entry;
    held.refs++;
    return () => {
        held.refs--;
        if (held.refs > 0)
            return;
        held.release();
        bySite.delete(siteCode);
    };
}
/** Refetch every `activityQueryKey(site)` query when the site reports new activity. */
export function useActivityLive(siteCode) {
    const queryClient = useQueryClient();
    useEffect(() => {
        if (!siteCode)
            return;
        return acquireActivityLive(queryClient, siteCode);
    }, [queryClient, siteCode]);
}
//# sourceMappingURL=useActivityLive.js.map